/**
 * QboTile — tuile « QuickBooks » d'un dossier Tenue de livres : en-tête (compagnie connectée, dernières données, à recevoir / à payer,
 * « Actualiser »), puis l'onglet courant : Rapports (lus en direct chez Intuit) · Clients · Fournisseurs · Factures · Taxes (copies locales).
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Link2, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg, money } from "../../comptabilite/_shared";
import { useQboConnection } from "./QboSection";
import { fmtDateTime, qboBase } from "./_qbo";
import QboReportsTab from "./QboReportsTab";
import QboPartiesTab from "./QboPartiesTab";
import QboDocsTab from "./QboDocsTab";
import QboTaxesTab from "./QboTaxesTab";

const nb = (n, one, many) => `${n ?? 0} ${(n ?? 0) > 1 ? (many || `${one}s`) : one}`;

function NotConnected({ conn, masterId, goTab }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const r = await api.post(`${qboBase(masterId)}/authorize`); window.location.assign(r.data.url); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-center space-y-3" data-testid="qbo-not-connected">
      <div className="w-12 h-12 rounded-xl bg-[#2CA01C] text-white font-black flex items-center justify-center text-lg mx-auto">qb</div>
      <h3 className="font-bold text-slate-800">Ce dossier n'est pas connecté à QuickBooks en ligne</h3>
      <p className="text-sm text-slate-500 max-w-lg mx-auto">
        {conn.configured
          ? "Connectez la compagnie QuickBooks du client pour lire ici ses rapports (résultats, bilan, taxes, âges des comptes), ses clients, fournisseurs et factures."
          : "Les clés Intuit ne sont pas configurées sur le serveur (QBO_CLIENT_ID / QBO_CLIENT_SECRET)."}
      </p>
      <div className="flex justify-center gap-2 flex-wrap">
        <Btn icon={Link2} onClick={connect} busy={busy} disabled={!conn.configured} testId="qbo-tile-connect-btn">Connecter QuickBooks</Btn>
        <Btn tone="slate" icon={Settings2} onClick={() => goTab("plan")} testId="qbo-tile-settings-btn">Réglages QuickBooks (plan comptable)</Btn>
      </div>
    </div>
  );
}

function Header({ conn, summary, onRefresh, busy }) {
  const es = summary?.entities_sync;
  return (
    <div className="bg-white border rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap" data-testid="qbo-tile-header">
      <div className="w-9 h-9 rounded-lg bg-[#2CA01C] text-white font-black flex items-center justify-center text-sm shrink-0">qb</div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-bold text-slate-800 text-sm" data-testid="qbo-tile-company">{conn.company_name || `Compagnie ${conn.realm_id}`}
          {conn.environment === "sandbox" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="qbo-tile-env">bac à sable</span>}
          {conn.needs_reconnect && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> reconnexion requise</span>}
        </div>
        <div className="text-[11px] text-slate-500" data-testid="qbo-tile-last-sync">
          {es ? <>Données copiées le {fmtDateTime(es.at)} · {nb(es.counts?.customer, "client")} · {nb(es.counts?.vendor, "fournisseur")} · {nb(es.counts?.invoice, "facture")} · {nb(es.counts?.bill, "facture fournisseur", "factures fournisseurs")} · {nb(es.counts?.tax_code, "code de taxes", "codes de taxes")}</>
            : "Aucune copie locale pour l'instant — cliquez « Actualiser » pour lire les clients, fournisseurs, factures et taxes."}
        </div>
      </div>
      {summary && (
        <div className="flex gap-4 text-xs" data-testid="qbo-tile-kpis">
          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-400">À recevoir</div><div className="font-mono font-bold text-slate-800" data-testid="qbo-kpi-ar">{money(summary.ar.open)}</div>{summary.ar.overdue > 0 && <div className="text-[10px] text-rose-700">{money(summary.ar.overdue)} en retard</div>}</div>
          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-400">À payer</div><div className="font-mono font-bold text-slate-800" data-testid="qbo-kpi-ap">{money(summary.ap.open)}</div>{summary.ap.overdue > 0 && <div className="text-[10px] text-rose-700">{money(summary.ap.overdue)} en retard</div>}</div>
        </div>
      )}
      <Btn tone="sky" icon={RefreshCw} onClick={onRefresh} busy={busy} testId="qbo-tile-refresh-btn">Actualiser</Btn>
    </div>
  );
}

export default function QboTile({ masterId, settings, tab, goTab }) {
  const { conn, reload } = useQboConnection(masterId);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadSummary = useCallback(() => api.get(`${qboBase(masterId)}/entities/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null)), [masterId]);
  useEffect(() => { if (conn?.connected) loadSummary(); }, [conn, loadSummary]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${qboBase(masterId)}/entities/refresh`, {});
      const c = r.data.counts || {};
      toast.success(`QuickBooks : ${nb(c.customer, "client")} · ${nb(c.vendor, "fournisseur")} · ${nb(c.invoice, "facture")} · ${nb(c.bill, "facture fournisseur", "factures fournisseurs")} · ${nb(c.tax_code, "code de taxes", "codes de taxes")}`);
      await loadSummary(); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!conn) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!conn.connected) return <NotConnected conn={conn} masterId={masterId} goTab={goTab} />;
  const syncedAt = summary?.entities_sync?.at || "";
  return (
    <div className="space-y-4" data-testid="qbo-tile">
      <Header conn={conn} summary={summary} onRefresh={refresh} busy={busy} />
      {tab === "rapports" && <QboReportsTab masterId={masterId} conn={conn} settings={settings} />}
      {tab === "clients" && <QboPartiesTab key={`c${syncedAt}`} masterId={masterId} kind="customers" />}
      {tab === "fournisseurs" && <QboPartiesTab key={`v${syncedAt}`} masterId={masterId} kind="vendors" />}
      {tab === "factures" && <QboDocsTab key={`i${syncedAt}`} masterId={masterId} kind="invoices" />}
      {tab === "factures-fournisseurs" && <QboDocsTab key={`b${syncedAt}`} masterId={masterId} kind="bills" />}
      {tab === "taxes" && <QboTaxesTab key={`t${syncedAt}`} masterId={masterId} summary={summary} />}
    </div>
  );
}
